import * as React from "react";
import { CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ChallengeCampaignEventState,
  CompleteTaskMutation,
  TaskStatus,
} from "@/types";
import { QuestLogo } from "@/components";

export interface QuestButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "ref"> {
  asChild?: boolean;
  task: TaskStatus;
  buttonText: string;
  taskMutation: CompleteTaskMutation;
}

const CompletedCard = ({ points }: { points: string }) => (
  <div className="w-full relative bg-gradient-to-br from-swapper-success/10 to-swapper-success/5 border-2 border-swapper-success/30 rounded-xl p-6 shadow-md">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="w-6 h-6 text-swapper-success" />
        <p className="text-sm text-swapper-secondary font-semibold">
          Quest completed successfully!
        </p>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-lg font-bold text-swapper-primary">+{points}</span>
        <QuestLogo size={16} />
      </div>
    </div>
  </div>
);

const PendingCard = () => (
  <div className="w-full relative bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border-2 border-yellow-500/30 rounded-xl p-6 shadow-md">
    <div className="flex items-center gap-3">
      <Clock className="w-6 h-6 text-yellow-600" />
      <p className="text-sm text-swapper-secondary font-medium">
        Your submission will be verified by a human. This can take some time.
      </p>
    </div>
  </div>
);

const CooldownCard = ({ remainingTime }: { remainingTime: number }) => {
  const [timeLeft, setTimeLeft] = React.useState(remainingTime);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const seconds = Math.ceil(timeLeft / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return (
    <div className="w-full relative bg-gradient-to-br from-gray-100 to-gray-50 border-2 border-gray-200 rounded-xl p-6 shadow-md">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Clock className="w-6 h-6 text-swapper-secondary/60" />
          <p className="text-sm text-swapper-secondary/70 font-medium">
            Cooldown active. Please wait before attempting again.
          </p>
        </div>
        <span className="text-lg font-bold text-swapper-secondary tabular-nums">
          {minutes > 0
            ? `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
            : `${seconds}s`}
        </span>
      </div>
    </div>
  );
};

export const QuestTaskButton = React.forwardRef<
  HTMLButtonElement,
  QuestButtonProps
>(({ taskMutation, task, buttonText, className, onClick }, ref) => {
  const [remainingTime, setRemainingTime] = React.useState(
    Math.max(0, (task.cooldownEndsAt || 0) - Date.now())
  );

  React.useEffect(() => {
    const timer = setInterval(() => {
      const newTime = Math.max(0, (task.cooldownEndsAt || 0) - Date.now());
      setRemainingTime(newTime);
    }, 1000);

    return () => clearInterval(timer);
  }, [task.cooldownEndsAt]);

  const isCooldownOver = remainingTime <= 0;

  const disabled =
    task.status === ChallengeCampaignEventState.PENDING ||
    task.status === ChallengeCampaignEventState.SUCCESSFUL ||
    !isCooldownOver ||
    taskMutation.isPending;

  const completed = task.status === ChallengeCampaignEventState.SUCCESSFUL;
  const pendingReview = task.status === ChallengeCampaignEventState.PENDING;
  const showCooldown = !isCooldownOver && remainingTime > 0;

  if (completed) {
    return <CompletedCard points={task.pointsReceived} />;
  }

  if (pendingReview) {
    return <PendingCard />;
  }

  if (showCooldown) {
    return <CooldownCard remainingTime={remainingTime} />;
  }

  return (
    <Button
      ref={ref}
      variant="primary"
      size="lg"
      disabled={disabled}
      onClick={onClick}
      className={cn("w-full", className)}
    >
      {taskMutation.isPending ? "Processing..." : buttonText || "Claim"}
    </Button>
  );
});

QuestTaskButton.displayName = "QuestTaskButton";
